<script setup>
import { computed } from 'vue';
import { useLayout } from '@/layout/admin/composables/layout';

const { layoutConfig } = useLayout();

const logoUrl = computed(() => {
    return `/layout/images/${layoutConfig.darkTheme.value ? 'logo-white' : 'logo-dark'}.png`;
});

const APP_NAME = import.meta.env.VITE_APP_NAME;
</script>

<template>
    <div class="surface-section px-4 md:px-6 lg:px-8 py-7">
        <div class="flex flex-column align-items-center text-center max-w-4xl mx-auto">
            <div class="mb-4">
                <img
                    :src="$store.state.setting?.image_url || logoUrl"
                    alt="Logo"
                    class="logo-img" />
            </div>

            <div class="flex align-items-center gap-4 mb-4">
                <a
                    v-if="$store.state.setting?.instagram"
                    class="cursor-pointer text-500 transition-colors transition-duration-150 hover:text-700"
                    :href="`https://instagram.com/${$store.state.setting?.instagram}`"
                    target="_blank"
                    rel="noopener noreferrer">
                    <i class="pi pi-instagram text-2xl"></i>
                </a>
                <a
                    v-if="$store.state.setting?.facebook"
                    class="cursor-pointer text-500 transition-colors transition-duration-150 hover:text-700"
                    :href="`https://www.facebook.com/${$store.state.setting?.facebook}`"
                    target="_blank"
                    rel="noopener noreferrer">
                    <i class="pi pi-facebook text-2xl"></i>
                </a>
                <a
                    v-if="$store.state.setting?.phone_number"
                    class="cursor-pointer text-500 transition-colors transition-duration-150 hover:text-700"
                    :href="`https://wa.me/${$store.state.setting?.phone_number}`"
                    target="_blank"
                    rel="noopener noreferrer">
                    <i class="pi pi-whatsapp text-2xl"></i>
                </a>
            </div>

            <!-- Bank Logos -->
            <div class="flex align-items-center gap-4 mb-4">
                <img
                    class="text-500 transition-colors transition-duration-150 hover:text-700"
                    src="/demo/images/bca-logo.png"
                    alt="BCA"
                    height="15" />
                <img
                    class="text-500 transition-colors transition-duration-150 hover:text-700"
                    src="/demo/images/mandiri-logo.png"
                    alt="Mandiri"
                    height="15" />
                <img
                    class="text-500 transition-colors transition-duration-150 hover:text-700"
                    src="/demo/images/bri-logo.png"
                    alt="BRI"
                    height="15" />
            </div>

            <div class="hidden sm:block text-sm text-600 line-height-3 mb-4">
                <span v-if="$store.state.setting?.app_address"> <strong>Alamat:</strong> {{ $store.state.setting.app_address }} </span>
                <span v-else> <strong>Alamat:</strong> Jalan Wates Sedayu Bantul Yogyakarta Indonesia </span>

                <span class="mx-3">|</span>

                <strong>Email:</strong>
                <a
                    :href="`mailto:${$store.state.setting?.app_email ?? 'hallo@xetoko.id'}`"
                    class="text-primary hover:underline ml-1">
                    {{ $store.state.setting?.app_email ?? 'hallo@xetoko.id' }}
                </a>

                <span class="mx-3">|</span>

                <strong>WhatsApp:</strong>
                <a
                    v-if="$store.state.setting?.phone_number"
                    :href="`https://wa.me/${$store.state.setting.phone_number}`"
                    class="text-primary hover:underline ml-1"
                    target="_blank"
                    rel="noopener noreferrer">
                    +{{ $store.state.setting.phone_number }}
                </a>
            </div>

            <div class="sm:hidden text-sm text-600 line-height-3 space-y-2 mb-4">
                <div v-if="$store.state.setting?.app_address"><strong>Alamat:</strong> {{ $store.state.setting.app_address }}</div>
                <div v-else><strong>Alamat:</strong> Jalan Wates Sedayu Bantul Yogyakarta Indonesia</div>

                <div>
                    <strong>Email:</strong>
                    <a
                        :href="`mailto:${$store.state.setting?.app_email ?? 'hallo@xetoko.id'}`"
                        class="text-primary hover:underline ml-1">
                        {{ $store.state.setting?.app_email ?? 'hallo@xetoko.id' }}
                    </a>
                </div>

                <div v-if="$store.state.setting?.phone_number">
                    <strong>WhatsApp:</strong>
                    <a
                        :href="`https://wa.me/${$store.state.setting.phone_number}`"
                        class="text-primary hover:underline ml-1"
                        target="_blank"
                        rel="noopener noreferrer">
                        +{{ $store.state.setting.phone_number }}
                    </a>
                </div>
            </div>

            <div class="text-sm text-500 line-height-3 mb-2">
                <router-link
                    class="text-900 font-medium inline-flex align-items-center cursor-pointer lg:pl-3 pr-1 hover:text-primary p-ripple"
                    data-pd-ripple="true"
                    :to="{ name: 'article' }">
                    <i
                        class="pi pi-book text-xl p-overlay-badge mr-2"
                        data-pd-badge="true"
                        data-p-overlay-badge="true"></i>
                    <span>Article</span>
                    <span
                        role="presentation"
                        aria-hidden="true"
                        data-p-ink="true"
                        data-p-ink-active="false"
                        class="p-ink"
                        data-pc-name="ripple"
                        data-pc-section="root"></span>
                </router-link>
            </div>

            <div class="text-sm text-500 line-height-3">© {{ new Date().getFullYear() }} {{ $store.state.setting?.app_name || 'XETOKO' }}. All rights reserved</div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'UserAppFooter'
};
</script>

<style scoped>
.text-primary {
    color: var(--primary-color, #007bff);
    text-decoration: none;
}

.text-primary:hover {
    text-decoration: underline;
}

.space-y-2 > * + * {
    margin-top: 0.5rem;
}

/* Icon hover effects */
.pi:hover {
    transform: scale(1.1);
    transition: transform 0.2s ease;
}

.logo-img {
    height: 40px;
}

@media (min-width: 768px) {
    .logo-img {
        height: 60px;
    }
}
</style>
